import type { Vue } from 'vue-facing-decorator';
import { Modal as BootstrapModal } from 'bootstrap';
import { globalState } from '../../app/global-state';

type VueType = typeof Vue.prototype;

interface IVue extends VueType { }
interface ModalDisplayArgs<T> {
	modal: BootstrapModal;
	data?: T;
	onHidden?: (response: ModalResponseData<T>) => void;
	onShown?: (e: ModalOnShownArgs) => void;
	onBeforeShown?: (e: ModalOnBeforeShownArgs) => void;
}

interface ModalHideArgs {
	modal: BootstrapModal;
}

interface ModalResponseData<T> {
	data?: T;
}

export interface ModalOnBeforeShownArgs {
	instance: IVue;
}

export interface ModalOnShownArgs {
	instance: IVue;
}

export class ModalUtils {
	private static _onBeforeShownHandlers: ((args: { modalHandler: any; modalSelector: JQuery }) => void)[];

	static addOnBeforeShownHandler(handler: (args: { modalHandler: any; modalSelector: JQuery }) => void) {
		if (ModalUtils._onBeforeShownHandlers == null) {
			ModalUtils._onBeforeShownHandlers = [];
		}

		ModalUtils._onBeforeShownHandlers.push(handler);
	}

	static showModal<T>(instance: IVue, dataArgs: ModalDisplayArgs<T> | IVue | Element): void {
		let args: ModalDisplayArgs<T> = dataArgs as any;
		if (args.modal == null) {
			args = { modal: args as any } as ModalDisplayArgs<T>;
		}

		const modalContext = args.modal;
		if (modalContext == null) {
			console.error('Modal context not specified');
			return;
		}

		let nextTick = instance?.$nextTick;
		if (nextTick == null) {
			nextTick = (cb) => {
				cb();
			};
		}

		nextTick(() => {
			const modalSelector = this._getModalJquery((modalContext as any)._element);
			modalSelector.off('hidden.bs.modal');
			modalSelector.off('shown.bs.modal');

			if (args.onHidden != null) {
				modalSelector.on('hidden.bs.modal', () => {
					args.onHidden({
						data: args.data,
					});
				});
			}

			if (args.onShown != null) {
				modalSelector.on('shown.bs.modal', () => {
					args.onShown({
						instance,
					});
				});
			}

			if (args.onBeforeShown != null) {
				args.onBeforeShown({
					instance,
				});
			}

			if ((args.modal as any)._config == null) {
				args.modal = BootstrapModal.getOrCreateInstance((args.modal as any)?.length > 0 ? (args.modal as any)[0] : (args.modal as any) as Element);
			}

			if (ModalUtils._onBeforeShownHandlers) {
				const modalHandler = args.modal;
				for (const hook of ModalUtils._onBeforeShownHandlers) {
					hook({ modalHandler, modalSelector });
				}

				args.modal.show();
			} else {
				args.modal.show();
			}
		});
	}

	static hideModal(dataArgs: ModalHideArgs | IVue | Element): void {
		let args: ModalHideArgs = dataArgs as any;
		if (args.modal == null) {
			args = { modal: args as any } as ModalHideArgs;
		}

		const modalContext = args.modal;
		if (modalContext == null) {
			console.error('Modal context not found');
			return;
		}

		if ((args.modal as any)._config == null) {
			args.modal = BootstrapModal.getOrCreateInstance((args.modal as any)?.length > 0 ? (args.modal as any)[0] : (args.modal as any) as Element);
		}

		setTimeout(() => {
			args.modal.hide();
		}, 1);
	}

	static hideAllOpenModals(): void {
		const allModals = document.querySelectorAll('.modal.show');
		if (allModals.length > 0) {
			for (let i = 0; i < allModals.length; i++) {
				const element = allModals[i];
				ModalUtils.hideModal(element);
			}
		}
	}

	static destroyModalInstance(element: string | Element) {
		if (element == null || !globalState.windowExists) {
			return;
		}

		const modalSelector = this._getModalJquery(element as any);
		modalSelector.off('hidden.bs.modal');
		modalSelector.off('shown.bs.modal');

		const modalInstance = BootstrapModal.getInstance(element);
		if (modalInstance == null) {
			return;
		}

		modalSelector.removeClass('show');
		const scrollBar = (modalInstance as any)._scrollBar;
		modalInstance.dispose();

		const openModal = document.querySelectorAll('.modal.show');
		if (openModal.length == 0) {
			document.body.classList.remove('modal-open');
			scrollBar.reset();
		}
	}

	static handleModalMountDelay(instance: IVue, cb: () => void) {
		if (instance?.$el?.closest('.modal-dialog')) {
			instance.$nextTick(() => {
				instance.$nextTick(() => {
					setTimeout(() => {
						cb();
					}, 100);
				});
			});
		} else {
			cb();
		}
	}

	static bindModalBottomSheetHandle(getModal: () => HTMLElement, hideModal: () => void) {
		if ((getModal() as any)?._dragBound) {
			return;
		}

		setTimeout(() => {
			const modal = getModal() as HTMLElement;
			const modalDialog = modal.querySelector('.modal-dialog') as HTMLElement;
			const dragHandle = modal.querySelector('.drag-handle');

			let startY = 0;
			let currentY = 0;
			let isDragging = false;
			const threshold = 100;
			const cancelDragging = () => {
				isDragging = false;
				modal.classList.remove('modal-is-dragging');
			};

			const startDragging = () => {
				isDragging = true;
				modal.classList.add('modal-is-dragging');
			};

			const onMove = (y) => {
				const translateY = Math.max(y - startY, 0);
				modalDialog.style.transform = `translateY(${translateY}px)`;
			};

			const endDrag = () => {
				cancelDragging();
				const dragDistance = currentY - startY;

				if (dragDistance > threshold) {
					hideModal();
				} else {
					modalDialog.style.transform = 'translateY(0)';
				}
			};

			// Touch events
			dragHandle.addEventListener('touchstart', (e) => {
				startY = (e as TouchEvent).touches[0].clientY;
				startDragging();
			});

			dragHandle.addEventListener('touchmove', (e) => {
				if (!isDragging) {
					return;
				}

				currentY = (e as TouchEvent).touches[0].clientY;
				onMove(currentY);
			});

			dragHandle.addEventListener('touchend', endDrag);

			// Mouse events
			dragHandle.addEventListener('mousedown', (e) => {
				startY = (e as MouseEvent).clientY;
				startDragging();

				const onMouseMove = (e) => {
					if (!isDragging) {
						return;
					}

					currentY = e.clientY;
					onMove(currentY);
					e.preventDefault();
					e.stopPropagation();
					e.stopImmediatePropagation();
				};

				const onMouseUp = () => {
					endDrag();
					globalState.removeEventListener('mousemove', onMouseMove);
					globalState.removeEventListener('mouseup', onMouseUp);
				};

				globalState.addEventListener('mousemove', onMouseMove);
				globalState.addEventListener('mouseup', onMouseUp);
			});

			// Reset on modal hide
			modal.addEventListener('hidden.bs.modal', () => {
				cancelDragging();
				modalDialog.style.transform = '';
			});

			(modal as any)._dragBound = true;
		}, 100);
	}

	private static _getModalJquery(modalElem: Element | IVue): JQuery {
		return globalState.jQuery(modalElem as any);
	}
}
